
import { useState } from "react";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Upload, X, Image } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface CreateArticleFormProps {
  onCreateArticle: (title: string, content: string, imageUrl?: string) => Promise<boolean>;
}

export const CreateArticleForm = ({ onCreateArticle }: CreateArticleFormProps) => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Vänligen välj en bildfil');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Bilden är för stor. Max storlek är 5MB');
      return;
    }

    setImageFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const uploadImage = async (): Promise<string | null> => {
    if (!imageFile) return null;

    try {
      const fileExt = imageFile.name.split('.').pop();
      const filePath = `articles/${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, imageFile);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('documents')
        .getPublicUrl(filePath);

      return data.publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUploading(true);

    try {
      let imageUrl: string | undefined;

      if (imageFile) {
        imageUrl = await uploadImage() || undefined;
      }

      const success = await onCreateArticle(title, content, imageUrl);
      
      if (success) {
        setTitle("");
        setContent("");
        setImageFile(null);
        setImagePreview(null);
      }
    } catch (error) {
      console.error('Error creating article:', error);
      toast.error('Det gick inte att ladda upp bilden');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl text-[#1B365D]">Skapa ny artikel</CardTitle>
        <p className="text-sm text-[#2F80ED]/80">
          Lägg till en ny artikel för att dela med organisationen
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Titel</Label>
            <Input
              id="title"
              placeholder="Skriv artikelns titel..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="content">Innehåll</Label>
            <Textarea
              id="content"
              placeholder="Skriv artikelns innehåll..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              required
              className="min-h-[120px]"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="image">Bild (valfritt)</Label>
            
            {imagePreview ? (
              <div className="relative">
                <img 
                  src={imagePreview} 
                  alt="Preview" 
                  className="w-full h-48 object-cover rounded-lg border border-[#E6EEF5]"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2"
                  onClick={removeImage}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-[#E6EEF5] rounded-lg p-6">
                <div className="text-center">
                  <Image className="mx-auto h-12 w-12 text-[#2F80ED]/40 mb-4" />
                  <div className="space-y-2">
                    <p className="text-sm text-[#1B365D]/60">
                      Lägg till en bild till din artikel
                    </p>
                    <p className="text-xs text-[#2F80ED]/40">
                      PNG, JPG upp till 5MB. Om ingen bild väljs används en standardbild.
                    </p>
                  </div>
                  <Input
                    id="image"
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="mt-4"
                    onClick={() => document.getElementById('image')?.click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Välj bild
                  </Button>
                </div>
              </div>
            )}
          </div>

          <Button 
            type="submit" 
            disabled={isUploading}
            className="w-full bg-[#1B365D] hover:bg-[#2F80ED]"
          >
            {isUploading ? 'Skapar artikel...' : 'Skapa artikel'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
